'use client';

import { MfaProviderSchema } from '@terra/shared-schemas-orchestrator';
import type { MfaMessageDto } from '@terra/shared-contracts-applications';
import type {
  MfaCredentialBinding,
  MfaFetchWebhookConfigFn,
  MfaProvider,
} from './mfa.types';
import { OtpDisplay } from './OtpDisplay';
import { EmailDisplay } from './EmailDisplay';
import { SmsDisplay } from './SmsDisplay';
import { WebhookDisplay } from './WebhookDisplay';

type MfaStatusDisplayProps = MfaCredentialBinding & {
  mfaProvider: MfaProvider;
  otpCode?: string;
  isOtpLoading?: boolean;
  onRefreshOtp?: () => void;
  messages?: MfaMessageDto[];
  isLoadingMessages?: boolean;
  onFetchMessages?: () => void;
  onFetchWebhookConfig?: MfaFetchWebhookConfigFn;
};

export function MfaStatusDisplay({
  mfaProvider,
  otpCode,
  isOtpLoading,
  onRefreshOtp,
  messages,
  isLoadingMessages,
  onFetchMessages,
  applicationId,
  credentialId,
  onFetchWebhookConfig,
}: MfaStatusDisplayProps) {
  switch (mfaProvider.providerType) {
    case MfaProviderSchema.enum.OTP:
      return (
        <OtpDisplay
          code={otpCode || ''}
          isLoading={isOtpLoading}
          onRefresh={onRefreshOtp}
          disabled={!mfaProvider.enabled}
        />
      );

    case MfaProviderSchema.enum.EMAIL: {
      const { emailAddress, enabled } = mfaProvider;
      if (!emailAddress) return null;
      return (
        <EmailDisplay
          emailAddress={emailAddress}
          disabled={!enabled}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          onFetchMessages={onFetchMessages}
        />
      );
    }

    case MfaProviderSchema.enum.SMS: {
      const { phoneNumber, enabled } = mfaProvider;
      if (!phoneNumber) return null;
      return (
        <SmsDisplay
          phoneNumber={phoneNumber}
          disabled={!enabled}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          onFetchMessages={onFetchMessages}
        />
      );
    }

    case MfaProviderSchema.enum.WEBHOOK: {
      const { id: mfaConfigId, providerMetadata, enabled } = mfaProvider;
      return (
        <WebhookDisplay
          applicationId={applicationId}
          credentialId={credentialId}
          mfaConfigId={mfaConfigId}
          providerMetadata={providerMetadata}
          disabled={!enabled}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          onFetchMessages={onFetchMessages}
          onFetchWebhookConfig={onFetchWebhookConfig}
        />
      );
    }

    default:
      return null;
  }
}

export type { MfaStatusDisplayProps };
